/**
 * Resolve class names for browser LiteRT inference.
 * Prefer model.meta.labels; otherwise labels.txt on the model; otherwise
 * labels packed into the .tflite as a TFLite associated file (ZIP append).
 */

export function parseLabelsTxt(text) {
  if (!text || typeof text !== 'string') {
    return [];
  }
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function parseMeta(model) {
  if (!model || !model.meta) {
    return {};
  }
  try {
    const meta = typeof model.meta === 'string' ? JSON.parse(model.meta) : model.meta;
    return meta && typeof meta === 'object' ? meta : {};
  } catch {
    return {};
  }
}

function basename(storedPath) {
  const parts = String(storedPath || '').split('/');
  return parts[parts.length - 1] || '';
}

/** Display name after uuid_ prefix (matches modelFiles.displayName). */
function displayName(storedPath) {
  const parts = String(storedPath || '').split('_');
  return parts[parts.length - 1] || '';
}

/**
 * Labels from model.meta — empty / missing arrays are treated as absent so
 * callers can fall back to labels.txt or embedded TFLite labels.
 */
export function labelsFromModelMeta(model) {
  const meta = parseMeta(model);
  if (Array.isArray(meta.labels) && meta.labels.length > 0) {
    return meta.labels.map((l) => String(l));
  }
  if (typeof meta.labels === 'string' && meta.labels.trim()) {
    try {
      const parsed = JSON.parse(meta.labels);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((l) => String(l));
      }
    } catch {
      // Plain newline-separated string
      const lines = parseLabelsTxt(meta.labels);
      if (lines.length > 0) {
        return lines;
      }
    }
  }
  return [];
}

export function getLabelsTxtBasename(model) {
  const meta = parseMeta(model);
  const files = Array.isArray(meta.files) ? meta.files : [];
  for (const f of files) {
    const name = displayName(f);
    if (name.toLowerCase() === 'labels.txt' || basename(f).toLowerCase().endsWith('labels.txt')) {
      return basename(f);
    }
  }
  return '';
}

export function browserLabelsTxtUrl(model, projectId, token) {
  const name = getLabelsTxtBasename(model);
  if (!name || !projectId) {
    return '';
  }
  const base = `/api/protected/models/${encodeURIComponent(projectId)}/${encodeURIComponent(name)}`;
  if (!token) {
    return base;
  }
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}token=${encodeURIComponent(token)}`;
}

function readU32LE(view, offset) {
  return view.getUint32(offset, true);
}

function readU16LE(view, offset) {
  return view.getUint16(offset, true);
}

/**
 * TFLite associated files are appended as a ZIP. Extract labels.txt / labelmap.txt
 * when present (issue #99). Returns [] if the buffer is not a ZIP or has no labels.
 */
export function extractLabelsFromTfliteBytes(bytes) {
  if (!bytes || bytes.byteLength < 22) {
    return [];
  }
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const view = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);

  // Find End of Central Directory (EOCD) signature 0x06054b50 near the end.
  let eocd = -1;
  const start = Math.max(0, u8.byteLength - 65557);
  for (let i = u8.byteLength - 22; i >= start; i -= 1) {
    if (readU32LE(view, i) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) {
    return [];
  }

  const totalEntries = readU16LE(view, eocd + 10);
  let centralOffset = readU32LE(view, eocd + 16);
  if (centralOffset + 46 > u8.byteLength) {
    return [];
  }

  const decoder = new TextDecoder('utf-8');
  for (let n = 0; n < totalEntries; n += 1) {
    if (centralOffset + 46 > u8.byteLength || readU32LE(view, centralOffset) !== 0x02014b50) {
      break;
    }
    const method = readU16LE(view, centralOffset + 10);
    const compSize = readU32LE(view, centralOffset + 20);
    const nameLen = readU16LE(view, centralOffset + 28);
    const extraLen = readU16LE(view, centralOffset + 30);
    const commentLen = readU16LE(view, centralOffset + 32);
    const localHeaderOffset = readU32LE(view, centralOffset + 42);
    const nameBytes = u8.subarray(centralOffset + 46, centralOffset + 46 + nameLen);
    const name = decoder.decode(nameBytes).replace(/^.*\//, '').toLowerCase();
    centralOffset += 46 + nameLen + extraLen + commentLen;

    if (name !== 'labels.txt' && name !== 'labelmap.txt') {
      continue;
    }
    if (localHeaderOffset + 30 > u8.byteLength || readU32LE(view, localHeaderOffset) !== 0x04034b50) {
      continue;
    }
    const localNameLen = readU16LE(view, localHeaderOffset + 26);
    const localExtraLen = readU16LE(view, localHeaderOffset + 28);
    const dataStart = localHeaderOffset + 30 + localNameLen + localExtraLen;
    const dataEnd = dataStart + compSize;
    if (dataEnd > u8.byteLength) {
      continue;
    }
    // Only store (method 0) is supported without a deflate dependency.
    if (method !== 0) {
      continue;
    }
    const content = decoder.decode(u8.subarray(dataStart, dataEnd));
    const labels = parseLabelsTxt(content);
    if (labels.length > 0) {
      return labels;
    }
  }
  return [];
}

async function fetchTextIfOk(url) {
  if (!url) {
    return '';
  }
  try {
    const res = await fetch(url);
    if (!res.ok) {
      return '';
    }
    return await res.text();
  } catch {
    return '';
  }
}

/**
 * Resolve class names for LiteRT browser runs.
 * @param {object} opts
 * @param {object} [opts.model] model with meta
 * @param {string|number} [opts.projectId]
 * @param {string} [opts.token]
 * @param {ArrayBuffer|Uint8Array} [opts.tfliteBytes] optional already-fetched model bytes
 * @param {string[]} [opts.preferred] labels already known (e.g. from public meta)
 */
export async function resolveBrowserModelLabels(opts = {}) {
  const preferred = Array.isArray(opts.preferred) ? opts.preferred.filter(Boolean) : [];
  if (preferred.length > 0) {
    return preferred.map(String);
  }
  const fromMeta = labelsFromModelMeta(opts.model);
  if (fromMeta.length > 0) {
    return fromMeta;
  }
  const labelsUrl = browserLabelsTxtUrl(opts.model, opts.projectId, opts.token);
  const fromFile = parseLabelsTxt(await fetchTextIfOk(labelsUrl));
  if (fromFile.length > 0) {
    return fromFile;
  }
  if (opts.tfliteBytes) {
    return extractLabelsFromTfliteBytes(opts.tfliteBytes);
  }
  return [];
}
